import { useCallback, useEffect, useState } from 'react';

import axios, { type AxiosResponse } from 'axios';

type Result<T> = {
  error?: Error;
  response?: AxiosResponse<T>;
};

/**
 * Usages:
 *
 * const [request] = useState(() => axios.get<User[]>('/users.json'));
 * const { loading, response, error } = useAxiosRequest(request);
 *
 * const [request, setRequest] = useState<Promise<AxiosResponse<User>> | undefined>();
 * useEffect(() => {
 *   setRequest(axios.get(`/users/${id}.json`));
 * }, [id])
 * const { loading, response, error } = useAxiosRequest(request);
 *
 * @param request must be stable
 */
export const useAxiosRequest = <T>(request?: Promise<AxiosResponse<T>>) => {
  const [result, setResult] = useState<Result<T> | undefined>();
  const [loading, setLoading] = useState<boolean | undefined>();

  useEffect(() => {
    setResult(undefined);
    setLoading(!!request);

    if (!request) return;

    let active = true;

    request.then((response) => {
      if (!active) return;
      setLoading(false);
      setResult({ response });
    });

    request.catch((error) => {
      if (!active) return;
      setLoading(false);
      setResult({ error });
    });

    return () => {
      active = false;
    };
  }, [request]);

  const refetch = useCallback(async () => {
    if (!request) throw new Error('useAxiosRequest: unable to refetch without a request assigned');

    const config = (await request).config;

    try {
      setLoading(true);
      const response: AxiosResponse<T> = await axios(config);
      setResult({ response });
    } catch (error) {
      setResult({ error: error as Error });
    } finally {
      setLoading(false);
    }
  }, [request]);

  return { ...result, loading, refetch };
};
